import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { RegistrationForm } from '../../types';
import { APP_CONFIG } from '../../utils/constants';
import apiService from '../../services/api';

// Step Components
import BasicInfoStep from '../../components/forms/BasicInfoStep';
import ProfileDetailsStep from '../../components/forms/ProfileDetailsStep';
import FamilyPreferencesStep from '../../components/forms/FamilyPreferencesStep';
import DocumentsStep from '../../components/forms/DocumentsStep';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Register: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<RegistrationForm>({
    step: 1,
    basicInfo: {
      name: '',
      email: '',
      phone: '',
      password: '',
      dob: '',
      gender: 'male'
    },
    profileDetails: {
      caste: 'reddy',
      subcaste: '',
      gothra: '',
      maritalStatus: 'never-married',
      height: '',
      complexion: 'fair',
      education: '',
      occupation: '',
      annualIncome: '',
      workLocation: {
        type: 'india',
        state: '',
        city: '',
        country: '',
        address: ''
      }
    },
    familyPreferences: {
      familyLocation: {
        state: '',
        city: '',
        address: ''
      },
      expectations: '',
      selfSummary: ''
    },
    documents: {
      photos: [],
      aadhaarNumber: '',
      aadhaarFile: undefined,
      panNumber: '',
      panFile: undefined,
      agreeTerms: false
    }
  });

  const navigate = useNavigate();

  const steps = [
    { number: 1, title: 'Basic Info', component: BasicInfoStep },
    { number: 2, title: 'Profile Details', component: ProfileDetailsStep },
    { number: 3, title: 'Family & Preferences', component: FamilyPreferencesStep },
    { number: 4, title: 'Documents', component: DocumentsStep }
  ];

  const updateFormData = (stepData: any) => {
    setFormData(prev => ({
      ...prev,
      ...stepData,
      step: currentStep
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const registrationData = {
        ...formData.basicInfo,
        ...formData.profileDetails,
        ...formData.familyPreferences,
        ...formData.documents,
        age: new Date().getFullYear() - new Date(formData.basicInfo.dob).getFullYear()
      };

      await apiService.register(registrationData);
      
      // Show success message and redirect to login
      alert('Registration successful! Please login to continue.');
      navigate('/login');
    } catch (error: any) {
      alert(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const StepComponent = steps[currentStep - 1].component;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">RPS</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Join {APP_CONFIG.name}
          </h1>
          <p className="text-gray-600">
            Create your profile in 4 simple steps
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`flex items-center space-x-2 ${
                  step.number <= currentStep ? 'text-primary-600' : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step.number < currentStep
                      ? 'bg-primary-600 text-white'
                      : step.number === currentStep
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {step.number < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <span className="text-sm font-medium hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Step {currentStep}: {steps[currentStep - 1].title}
          </h2>
          
          <StepComponent
            formData={formData}
            updateFormData={updateFormData}
          />
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Already have an account? Login
            </Link>
            
            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" text="" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Complete Registration</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;